import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { BrowserProfile } from '../services/loopService';

import './BrowserProfilePicker.css';

interface BrowserProfilePickerProps {
  profiles: BrowserProfile[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  compact?: boolean;
}

const statusLabel = (profile: BrowserProfile) => profile.status === 'online' ? 'Online' : 'Offline';
const availability = (profile: BrowserProfile) => {
  if (profile.status === 'online') return 'Connected and ready to run';
  const lastSeen = profile.last_seen_at && new Date(profile.last_seen_at);

  return lastSeen && !Number.isNaN(lastSeen.getTime())
    ? `Last seen ${lastSeen.toLocaleString()}` : 'Open this browser to reconnect';
};

const BrowserIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
  strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
  <rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4" />
</svg>;

// eslint-disable-next-line max-statements
const popupPosition = (trigger: HTMLButtonElement): React.CSSProperties => {
  const rect = trigger.getBoundingClientRect();
  const margin = 12;
  const gap = 6;
  const viewport = window.visualViewport;
  const bounds = viewport?.scale === 1 ? viewport
    : { width: window.innerWidth, height: window.innerHeight, offsetTop: 0, offsetLeft: 0 };
  const width = Math.max(0, Math.min(Math.max(rect.width, 300), bounds.width - margin * 2));
  const lowerEdge = bounds.offsetTop + bounds.height - margin;
  const upperEdge = bounds.offsetTop + margin;
  const anchorAbove = Math.max(upperEdge, Math.min(rect.top - gap, lowerEdge));
  const anchorBelow = Math.max(upperEdge, Math.min(rect.bottom + gap, lowerEdge));
  const below = lowerEdge - anchorBelow;
  const above = anchorAbove - upperEdge;
  const upward = below < 280 && above > below;

  return {
    width,
    left: Math.max(bounds.offsetLeft + margin, Math.min(rect.left, bounds.offsetLeft + bounds.width - width - margin)),
    ...(upward ? { bottom: window.innerHeight - anchorAbove } : { top: anchorBelow }),
    maxHeight: Math.max(0, Math.min(360, upward ? above : below)),
  };
};

const usePopup = (open: boolean, trigger: React.RefObject<HTMLButtonElement | null>,
  popup: React.RefObject<HTMLDivElement | null>, close: () => void) => {
  const [position, setPosition] = useState<React.CSSProperties>({});

  // eslint-disable-next-line max-statements
  useLayoutEffect(() => {
    if (!open) return undefined;
    const viewport = window.visualViewport;
    const update = (event?: Event) => {
      if (event && viewport && viewport.scale !== 1) return;
      if (trigger.current) setPosition(popupPosition(trigger.current));
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);

    return () => {
      window.removeEventListener('resize', update); window.removeEventListener('scroll', update, true);
      viewport?.removeEventListener('resize', update); viewport?.removeEventListener('scroll', update);
    };
  }, [open, trigger]);
  useEffect(() => {
    if (!open) return undefined;
    const outside = (event: Event) => {
      const target = event.target as Node;

      if (!trigger.current?.contains(target) && !popup.current?.contains(target)) close();
    };

    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside);
    document.addEventListener('focusin', outside);

    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('touchstart', outside);
      document.removeEventListener('focusin', outside);
    };
  }, [close, open, popup, trigger]);

  return position;
};

// eslint-disable-next-line max-lines-per-function, max-statements
export const BrowserProfilePicker: React.FC<BrowserProfilePickerProps> = ({
  profiles, value, onChange, disabled = false, compact = false,
}) => {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [previousDisabled, setPreviousDisabled] = useState(disabled);
  const [activeId, setActiveId] = useState(value);

  if (previousDisabled !== disabled) { setPreviousDisabled(disabled); setExpanded(false); }
  const visibleProfiles = profiles.filter((profile) => profile.status !== 'revoked');
  const choices = ['', ...visibleProfiles.map((profile) => profile.id)];
  const selected = visibleProfiles.find((profile) => profile.id === value);
  const open = expanded && !disabled;
  const active = Math.max(0, choices.indexOf(activeId));
  const close = React.useCallback(() => setExpanded(false), []);
  const position = usePopup(open, trigger, popup, close);
  const description = selected ? `${selected.name}, ${statusLabel(selected)}`
    : value ? 'Selected browser is unavailable' : 'No browser profile selected';
  const choose = (next: string) => { onChange(next); close(); trigger.current?.focus({ preventScroll: true }); };
  const show = (index = Math.max(0, choices.indexOf(value))) => { setActiveId(choices[index]); setExpanded(true); };
  // eslint-disable-next-line max-statements
  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const key = event.key;

    if (key === 'Tab') { close();

      return; }
    if (key === 'Escape') { if (open) { event.preventDefault(); event.stopPropagation(); close(); }

      return; }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(key)) return;
    event.preventDefault();
    if (key === 'Enter' || key === ' ') { if (open) choose(choices[active]); else show();

      return; }
    if (key === 'Home' || key === 'End') { show(key === 'Home' ? 0 : choices.length - 1);

      return; }
    if (!open) { show();

      return; }
    setActiveId(choices[Math.max(0, Math.min(choices.length - 1, active + (key === 'ArrowDown' ? 1 : -1)))]);
  };

  useEffect(() => {
    if (open) document.getElementById(`${id}-option-${active}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, id, open]);

  return <div className={`browser-profile-picker${compact ? ' browser-profile-picker--compact' : ''}`}>
    <button ref={trigger} type="button" role="combobox" className="browser-profile-picker__trigger"
      aria-label="Browser profile" aria-describedby={`${id}-selection`} aria-haspopup="listbox" aria-expanded={open}
      aria-controls={open ? `${id}-listbox` : undefined} aria-activedescendant={open ? `${id}-option-${active}` : undefined}
      data-profile-id={value} disabled={disabled} title={description} onKeyDown={onKeyDown}
      onClick={() => { if (open) close(); else show(); }}>
      <BrowserIcon />
      <span className="browser-profile-picker__name">{selected?.name || (value ? 'Unavailable profile' : compact ? 'Browser' : 'Choose a browser profile')}</span>
      {selected && (compact
        ? <span className={`browser-profile-picker__dot is-${selected.status}`} aria-hidden="true" />
        : <span className={`browser-profile-picker__status is-${selected.status}`}>
          <span className={`browser-profile-picker__dot is-${selected.status}`} aria-hidden="true" />{statusLabel(selected)}
        </span>)}
      <svg className="browser-profile-picker__chevron" width="12" height="12" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
    </button>
    <span id={`${id}-selection`} className="browser-profile-picker__sr-only">{description}</span>
    {open && createPortal(<div ref={popup} className="browser-profile-picker__popup" style={position}>
      <div className="browser-profile-picker__heading">Browser profiles</div>
      <ul id={`${id}-listbox`} role="listbox" aria-label="Browser profiles" className="browser-profile-picker__list">
        {[undefined, ...visibleProfiles].map((profile, index) => {
          const profileId = profile?.id || '';

          return <li key={profileId} id={`${id}-option-${index}`} role="option" aria-selected={value === profileId}
            data-profile-id={profileId} className={`browser-profile-picker__option${active === index ? ' is-active' : ''}`}
            onMouseDown={(event) => event.preventDefault()} onMouseEnter={() => setActiveId(profileId)}
            onClick={() => choose(profileId)}>
            <span className="browser-profile-picker__profile-icon"><BrowserIcon /></span>
            <span className="browser-profile-picker__details">
              <span className="browser-profile-picker__row">
                <span className="browser-profile-picker__name">{profile?.name || 'Choose a browser profile'}</span>
                {profile && <span className={`browser-profile-picker__status is-${profile.status}`}>
                  <span className={`browser-profile-picker__dot is-${profile.status}`} aria-hidden="true" />{statusLabel(profile)}
                </span>}
              </span>
              <span className="browser-profile-picker__availability">{profile ? availability(profile) : 'No browser selected'}</span>
            </span>
            <span className="browser-profile-picker__check" aria-hidden="true">{value === profileId ? '✓' : ''}</span>
          </li>;
        })}
      </ul>
    </div>, document.body)}
  </div>;
};
